/**
 * Helpers champs personnalisés de l'assistant d'import : catalogue des types
 * d'attribut V2 proposables et initialisation des décisions de mapping.
 */

import type { CustomFieldDecisions, CustomFieldsModel } from './types';

/**
 * Types `AttributeType` V2 proposables pour une colonne CSV (synchronisé avec
 * `CustomFieldColumnAiPremapper::ATTRIBUTE_TYPES` côté back). FILE/IMAGE et
 * les types composites (ADDRESS, PHONE, COORDINATES) sont exclus : un CSV ne
 * transporte pas de binaire et les composites sont des références UUID V2.
 */
export const CF_ATTRIBUTE_TYPES: Array<{ code: string; label: string }> = [
  { code: 'STRING', label: 'Texte court' },
  { code: 'TEXT_AREA', label: 'Texte long' },
  { code: 'INTEGER', label: 'Nombre entier' },
  { code: 'FLOAT', label: 'Nombre décimal' },
  { code: 'CURRENCY', label: 'Montant' },
  { code: 'PERCENTAGE', label: 'Pourcentage' },
  { code: 'BOOLEAN', label: 'Oui / Non' },
  { code: 'EMAIL', label: 'Email' },
  { code: 'URL', label: 'URL' },
  { code: 'DATE', label: 'Date' },
  { code: 'DATETIME', label: 'Date et heure' },
  { code: 'TIME', label: 'Heure' },
  { code: 'POSTAL_CODE', label: 'Code postal' },
  { code: 'IBAN', label: 'IBAN' },
  { code: 'COLOR', label: 'Couleur' },
  { code: 'SELECT', label: 'Choix unique' },
  { code: 'MULTI_SELECT', label: 'Choix multiple' },
  { code: 'RADIO', label: 'Boutons radio' },
  { code: 'CHECKBOX', label: 'Cases à cocher' },
];

export const CF_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  CF_ATTRIBUTE_TYPES.map((t) => [t.code, t.label]),
);

/** En-tête technique → libellé humain par défaut (`regime_alimentaire` → « Regime alimentaire »). */
export function humanizeColumn(column: string): string {
  const spaced = column.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : column;
}

/**
 * Décisions initiales : toutes les colonnes détectées sont proposées à
 * l'import (elles sont dans le fichier parce que l'école les a fournies),
 * avec le type suggéré par l'heuristique, un libellé dérivé de l'en-tête, et
 * le champ V2 existant pré-sélectionné quand le nom matche (pas de doublon).
 */
export function initCfDecisions(model: CustomFieldsModel): CustomFieldDecisions {
  const decisions: CustomFieldDecisions = {};
  for (const [logicalName, file] of Object.entries(model.files)) {
    decisions[logicalName] = {};
    for (const col of file.columns) {
      decisions[logicalName][col.column] = {
        action: 'import',
        typeCode: CF_TYPE_LABELS[col.suggestedType] ? col.suggestedType : 'STRING',
        label: humanizeColumn(col.column),
        existingAttributeId: col.existingMatch?.id ?? null,
      };
    }
  }
  return decisions;
}

/** Y a-t-il au moins une colonne à importer ? */
export function hasCfImports(decisions: CustomFieldDecisions): boolean {
  return Object.values(decisions).some((cols) =>
    Object.values(cols).some((d) => d.action === 'import'),
  );
}
